import { ref, watch } from 'vue';

/**
 * Per-device escape hatch from the URL-serialized cascade (ADR-0021 / ADR-0027).
 *
 * When `true`, detail panels that would otherwise open as inline cascade Columns
 * open as right-edge / bottom-sheet overlay Sidebars instead — the pre-cascade
 * behaviour, still fully alive as the second renderer. The preference is read at
 * the single routing chokepoint (`shouldRouteToCascade` in `sidebarStore`) and
 * governs only *new* opens: a URL that already encodes columns still hydrates the
 * strip, so deep-links and refresh are honoured regardless of this setting.
 *
 * Local-only by design (a viewing comfort choice, not a business rule): persisted
 * in `localStorage`, not synced across devices, no backend surface.
 *
 * Default is `false` → cascade stays the default experience. An empty/absent key
 * reads as `false`, so existing users and the e2e build (fresh storage) keep the
 * cascade without any migration.
 */

const STORAGE_KEY = 'therame.preferSidebars';

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // localStorage may be unavailable (privacy mode, sandboxed iframe) — fall
    // back to the cascade default rather than throwing at import time.
    return false;
  }
}

// Module-level singleton so the gate and the settings toggle share one reactive
// source of truth; every consumer sees the same value and the same flips.
const preferSidebars = ref<boolean>(readInitial());

watch(preferSidebars, (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Persisting failed (storage unavailable / quota) — keep the in-memory ref
    // so the current session still honours the choice.
  }
});

export function usePreferSidebars() {
  return preferSidebars;
}
